/**
 * La guarda de un umbral común de cadencia: se mide contra la flota, no contra
 * un vehículo (ADR-009).
 *
 * ## Por qué la guarda del p99 no sirve para la cadencia
 *
 * `threshold-guard.ts` compara cada umbral con el p99 de UN vehículo. Para un
 * perfil de ese vehículo tiene sentido; para el de toda una cadencia, no, y el
 * 06/10/2026 se vio por qué:
 *
 *  - un umbral en el p99 marca el 1 % de los pares del vehículo, y eso son
 *    varios episodios por cada 100 km. Las bandas del manual cortan en 1, 2 y 4.
 *    En la Fase 1, con el p99 de cada vehículo, los 48 vehículos con el mes
 *    completo quedaron en Alto (4) o Crítico (44) en aceleración y en frenada:
 *    la guarda solo deja guardar umbrales que condenan al vehículo de referencia;
 *  - al revés, el umbral común que pone al vehículo típico en la frontera
 *    Bajo/Medio (1,15 m/s² a 10 s) queda por encima del p99 de la mayoría, y la
 *    guarda lo rechaza aunque 58 de 63 vehículos de la cadencia sí lo alcancen;
 *  - y para pasarla basta escoger como referencia un vehículo brusco, que es
 *    justo lo contrario de lo que una guarda tiene que permitir.
 *
 * ## La pregunta correcta
 *
 * Para un umbral de toda la cadencia la pregunta no es «¿lo alcanza este
 * vehículo?» sino **«¿lo alcanza la cadencia?»**: en cuántos de sus vehículos,
 * con kilometraje suficiente, cada familia pudo dispararse. Si es menos de la
 * mitad, el umbral está fuera de lo que esa flota hace y sus ceros no dicen
 * nada; se rechaza. Si es la mitad o más, se acepta, y la cifra viaja dentro
 * del perfil (`fleetCoverage`) porque es la que después le da significado al
 * cero de un vehículo suave (`component-measurability.ts`).
 *
 * «Pudo dispararse» es la MISMA pregunta par a par que hace el informe
 * (`assessFiringChance`), más los episodios que sí se consolidaron. No se
 * escribe otra vez.
 *
 * Todo aquí es puro. Quién produce la evidencia —el servidor, vehículo por
 * vehículo y firmada— está en `server/calibration/fleet-evidence.ts`.
 */

import type { CandidateMeasurements } from "../pipeline/driving-analysis-v5.ts";
import type { AnalysisProfile, CadenceRegime, FleetCoverage } from "../pipeline/profiles.ts";
import {
  assessFiringChance,
  COMPONENT_ORDER,
  MINIMUM_FIRING_SHARE,
  MINIMUM_FLEET_DISTANCE_KM,
} from "../report/component-measurability.ts";
import { COMPONENT_LABELS, type ScoreComponent } from "../report/drivescore-v1.ts";

export { MINIMUM_FIRING_SHARE, MINIMUM_FLEET_DISTANCE_KM };

/**
 * Vehículos mínimos de la cadencia para que «la mitad» signifique algo. Con dos
 * vehículos, uno brusco basta para declarar normal cualquier umbral.
 */
export const MINIMUM_FLEET_VEHICLES = 5;

export type FleetFiring = Record<ScoreComponent, boolean>;

/** Lo que un vehículo aporta a la verificación. Sin placa: no hace falta. */
export type FleetEvidenceItem = {
  deviceId: string;
  /**
   * El régimen que la cadencia de ESTE vehículo le asigna en el período. `null`
   * cuando no cae en ninguno: sin cadencia medible, o por encima del techo de
   * maniobras.
   */
  regime: CadenceRegime | null;
  /** Kilómetros elegibles con los umbrales que se están verificando. */
  distanceKm: number;
  firing: FleetFiring;
};

/**
 * ¿Pudo dispararse cada familia en este vehículo con estos umbrales?
 *
 * Sí, si consolidó al menos un episodio, o si algún par llegó a lo que el
 * detector le exigía. Lo segundo es `assessFiringChance`, la misma pregunta del
 * informe. Cuando esa pregunta se abstiene —giro con aceleración lateral— solo
 * cuentan los episodios: para declarar normal un umbral hace falta haberlo visto
 * disparar, no basta con que no se pueda descartar.
 */
export function fleetFiring(input: {
  measurements: CandidateMeasurements;
  profile: AnalysisProfile;
  episodes: Record<ScoreComponent, number>;
}): FleetFiring {
  const chances = assessFiringChance(input.measurements, input.profile);
  const out = {} as FleetFiring;
  for (const component of COMPONENT_ORDER) {
    const chance = chances[component];
    out[component] =
      (input.episodes[component] ?? 0) > 0 ||
      (chance.couldFire === true && !chance.belowReferenceSpeed && chance.gateAlone !== false);
  }
  return out;
}

export type FleetCoverageCheck = {
  ok: boolean;
  regime: CadenceRegime;
  /** Vehículos de la cadencia con kilometraje suficiente: el denominador. */
  vehicles: number;
  firing: FleetCoverage["firing"];
  /** Lo que se dejó fuera, para que el rechazo y la pantalla lo puedan decir. */
  ignored: { otherRegime: number; shortDistance: number; duplicates: number };
  reasons: string[];
  message: string | null;
};

const half = (vehicles: number) => Math.ceil(vehicles * MINIMUM_FIRING_SHARE);

const compareText = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/**
 * Resume la evidencia de la flota y decide si el umbral común es de la cadencia.
 *
 * Determinística: se ordena por equipo antes de contar y un equipo repetido
 * cuenta una vez, así que el orden en que llegaron los lotes no cambia nada.
 * Si el mismo equipo llegó dos veces con contenido distinto —un lote repetido
 * en otro momento—, se queda la versión que ordena primero por su contenido
 * completo: arbitraria, pero siempre la misma (constitución, Art. 1).
 */
export function checkFleetCoverage(regime: CadenceRegime, items: FleetEvidenceItem[]): FleetCoverageCheck {
  const sorted = items
    .map((item) => ({ item, key: JSON.stringify([item.deviceId, item.regime, item.distanceKm, item.firing]) }))
    .sort((a, b) => compareText(a.item.deviceId, b.item.deviceId) || compareText(a.key, b.key))
    .map(({ item }) => item);

  const seen = new Set<string>();
  const ignored = { otherRegime: 0, shortDistance: 0, duplicates: 0 };
  const firing: FleetCoverage["firing"] = { velocidadAlta: 0, frenadas: 0, aceleraciones: 0, giros: 0 };
  let vehicles = 0;

  for (const item of sorted) {
    if (seen.has(item.deviceId)) {
      ignored.duplicates++;
      continue;
    }
    seen.add(item.deviceId);
    if (item.regime !== regime) {
      ignored.otherRegime++;
      continue;
    }
    if (!(item.distanceKm >= MINIMUM_FLEET_DISTANCE_KM)) {
      ignored.shortDistance++;
      continue;
    }
    vehicles++;
    for (const component of COMPONENT_ORDER) if (item.firing[component]) firing[component]++;
  }

  const reasons: string[] = [];
  if (vehicles < MINIMUM_FLEET_VEHICLES) {
    reasons.push(
      `Solo ${vehicles} vehículo(s) de la cadencia ${regime} tienen al menos ${MINIMUM_FLEET_DISTANCE_KM} km ` +
        `en el período, y hacen falta ${MINIMUM_FLEET_VEHICLES}. Con tan pocos, uno solo decide si el umbral ` +
        `es normal para la cadencia. Escoge un período más largo.`,
    );
  } else {
    const needed = half(vehicles);
    for (const component of COMPONENT_ORDER) {
      if (firing[component] >= needed) continue;
      reasons.push(
        `${COMPONENT_LABELS[component]}: con estos umbrales pudo dispararse en ${firing[component]} de ` +
          `${vehicles} vehículos de la cadencia ${regime} con al menos ${MINIMUM_FLEET_DISTANCE_KM} km, y hace ` +
          `falta la mitad (${needed}). Un umbral que casi nadie de la cadencia alcanza deja los ceros sin ` +
          `significado: en esos vehículos la familia no se mide, no sale bien.`,
      );
    }
  }

  return {
    ok: reasons.length === 0,
    regime,
    vehicles,
    firing,
    ignored,
    reasons,
    message: reasons.length ? reasons.join(" ") : null,
  };
}

/** La cobertura que viaja dentro del perfil guardado. */
export function toFleetCoverage(
  check: FleetCoverageCheck,
  period: { fromIso: string; toIso: string },
): FleetCoverage {
  return {
    fromIso: period.fromIso,
    toIso: period.toIso,
    vehicles: check.vehicles,
    firing: { ...check.firing },
    minimumDistanceKm: MINIMUM_FLEET_DISTANCE_KM,
  };
}
